import { Box, Button, Menu, MenuItem, Typography } from '@evaneos/b2b-ui';
import { DateCalendar } from '@evaneos/b2b-ui/date-picker';
import MoreMenuIcon from '@evaneos/b2b-ui/icons/MoreVert';
import AirPlaneTicketIcon from '@evaneos/b2b-ui/icons/AirplaneTicketOutlined';
import CancelIcon from '@evaneos/b2b-ui/icons/CancelOutlined';
import HourglassIcon from '@evaneos/b2b-ui/icons/HourglassEmptyRounded';

import { useIntl } from 'react-intl';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { addYears, formatISO } from 'date-fns';

import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../shared/analytics';
import { useGetDossierData } from '../../../resources/dossier/queries';
import { DossierId } from '../../../resources/dossier/types';
import {
    ARCHIVE_PROCESS,
    CANCELLED_STEP,
    CLOSED_STEP,
    DEFERRED_TRIP_STEP,
    LOST_STEP,
    POSTPONED_STEP,
    TRIP_HAS_ENDED_STEP,
} from '../../../domain';
import { StepName } from '../../../design-system/types';
import { useChangeStepMutation, usePostponeMutation } from '../../../resources/dossier/mutations';
import { getToday } from '../../../shared/utilities/date';
import { useGetOptionWayUrl } from '../../../resources/transport-partnership';

import { StepChangeDialog } from './StepChangeDialog';

/* eslint-disable max-lines-per-function -- options menu with multiple modals, mutations and dialog state */
export const MoreOptionsMenu = () => {
    const { formatMessage } = useIntl();
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const changeStepMutation = useChangeStepMutation();
    const postponeMutation = usePostponeMutation(dossierId);
    const optionWayUrlMutation = useGetOptionWayUrl();
    const initialDate = getToday();

    const { process, step: currentStep } = useGetDossierData(dossierId)?.dossier || {};

    const [openedModal, setOpenedModal] = useState<ModalAllowedStep | false>(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    if (process === ARCHIVE_PROCESS) {
        return null;
    }

    const handleMoreMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMoreMenuClose = () => {
        setAnchorEl(null);
    };

    const handleModalOpen = (modal: ModalAllowedStep): void => {
        handleMoreMenuClose();
        setOpenedModal(modal);
    };

    const handleModalClose = () => {
        setOpenedModal(false);
    };

    const changeStep = (nextStep: StepName) => {
        if (!currentStep) return;

        if (nextStep === LOST_STEP) {
            trackEvent((ampli, defaultProperties) => {
                ampli.markTheFileAsLostSubmitted({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }

        changeStepMutation.mutate({ dossierId, nextStep });
        handleModalClose();
    };

    const postpone = () => {
        if (!(selectedDate instanceof Date)) {
            return;
        }
        postponeMutation.mutate({ dueDate: formatISO(selectedDate) });
        handleModalClose();
    };

    return (
        <Box>
            <Button
                variant="outlined"
                aria-label={formatMessage({ id: 'global.more_options' })}
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleMoreMenuClick}
                sx={{
                    p: 1,
                    minWidth: 0,
                    width: '40px',
                    height: '40px',
                }}
            >
                <MoreMenuIcon />
            </Button>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleMoreMenuClose}
                slotProps={{
                    list: {
                        'aria-labelledby': 'basic-button',
                    },
                    paper: {
                        sx: {
                            '& .MuiList-root': {
                                paddingTop: 0,
                                paddingBottom: 0,
                                '& .MuiMenuItem-root': {
                                    gap: 0.5,
                                    paddingY: 2,
                                    fontSize: '1.4rem',
                                    lineHeight: '2rem',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': {
                                        borderBottom: 'none',
                                    },
                                },
                            },
                        },
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.optionWayClicked({
                                ...defaultProperties,
                                ...trackedTripProperties,
                            });
                        });
                        optionWayUrlMutation.mutate();
                    }}
                    sx={{ gap: 1 }}
                >
                    <AirPlaneTicketIcon sx={{ fontSize: '16px' }} aria-hidden="true" />
                    {formatMessage({ id: 'dossier.transport_partnership.option_way' })}
                </MenuItem>
                {process === 'sales' && currentStep !== POSTPONED_STEP && (
                    <MenuItem
                        data-testid={POSTPONED_STEP}
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.postponeRequestClicked({
                                    ...defaultProperties,
                                    ...trackedTripProperties,
                                });
                            });
                            handleModalOpen(POSTPONED_STEP);
                        }}
                    >
                        <HourglassIcon sx={{ fontSize: '16px' }} aria-hidden="true" />
                        {formatMessage({ id: 'dossier.postponed.request' })}
                    </MenuItem>
                )}
                {process === 'sales' && (
                    <MenuItem data-testid={LOST_STEP} onClick={() => handleModalOpen(LOST_STEP)}>
                        <CancelIcon color="error" sx={{ fontSize: '16px' }} aria-hidden="true" />
                        <Typography color="error" fontSize="inherit" lineHeight="inherit">
                            {formatMessage({ id: 'dossier.lost.request' })}
                        </Typography>
                    </MenuItem>
                )}
                {process === 'operation' &&
                    currentStep !== DEFERRED_TRIP_STEP &&
                    currentStep !== TRIP_HAS_ENDED_STEP && (
                        <MenuItem
                            data-testid={DEFERRED_TRIP_STEP}
                            onClick={() => handleModalOpen(DEFERRED_TRIP_STEP)}
                        >
                            <HourglassIcon sx={{ fontSize: '16px' }} aria-hidden="true" />
                            {formatMessage({ id: 'dossier.deferred.request' })}
                        </MenuItem>
                    )}
                {process === 'operation' && currentStep !== TRIP_HAS_ENDED_STEP && (
                    <MenuItem
                        data-testid={CANCELLED_STEP}
                        onClick={() => handleModalOpen(CANCELLED_STEP)}
                    >
                        <CancelIcon color="error" sx={{ fontSize: '16px' }} aria-hidden="true" />
                        <Typography color="error" fontSize="inherit" lineHeight="inherit">
                            {formatMessage({ id: 'dossier.cancelled.request' })}
                        </Typography>
                    </MenuItem>
                )}
                {currentStep === TRIP_HAS_ENDED_STEP && (
                    <MenuItem
                        data-testid={CLOSED_STEP}
                        onClick={() => handleModalOpen(CLOSED_STEP)}
                    >
                        <CancelIcon color="error" sx={{ fontSize: '16px' }} aria-hidden="true" />
                        <Typography color="error" fontSize="inherit" lineHeight="inherit">
                            {formatMessage({ id: 'dossier.closed.request' })}
                        </Typography>
                    </MenuItem>
                )}
            </Menu>
            <StepChangeDialog
                isOpen={openedModal === POSTPONED_STEP}
                onClose={handleModalClose}
                onConfirm={postpone}
                stepType={POSTPONED_STEP}
                confirmDisabled={!selectedDate}
                data-testid={`modal-${POSTPONED_STEP}`}
            >
                <DateCalendar
                    value={selectedDate}
                    onChange={(day) => setSelectedDate(day)}
                    minDate={initialDate}
                    maxDate={addYears(initialDate, 3)}
                    sx={{
                        '& .MuiPickerDay-root': {
                            fontSize: '14px',
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                            fontSize: '14px',
                        },
                    }}
                />
            </StepChangeDialog>

            {[LOST_STEP, CANCELLED_STEP, DEFERRED_TRIP_STEP, CLOSED_STEP].map((stepType) => (
                <StepChangeDialog
                    key={stepType}
                    isOpen={openedModal === stepType}
                    onClose={handleModalClose}
                    onConfirm={() => changeStep(stepType)}
                    stepType={stepType}
                    data-testid={`modal-${stepType}`}
                />
            ))}
        </Box>
    );
};
/* eslint-enable max-lines-per-function */

export type ModalAllowedStep =
    | typeof POSTPONED_STEP
    | typeof CANCELLED_STEP
    | typeof LOST_STEP
    | typeof CLOSED_STEP
    | typeof DEFERRED_TRIP_STEP;
